import React, { useEffect, useRef } from "react";
import useWhyDidYouRender from "../hooks/useWhyDidYouRender";
import { UserLocation } from "../types/mapData";

interface MapContainerProps {
  userLocation: UserLocation | null;
  widget: boolean;
  onMapLoad: (map: google.maps.Map) => void;
}

/**
 * MapContainer - Lightweight wrapper around Google Maps API.
 *
 * Responsibilities:
 * - Creates and renders the initial map instance once user location is available.
 * - Passes the map instance back to parent via onMapLoad callback.
 * - Defensively handles missing SDKs or invalid coordinates.
 *
 *   ┌────────────┐
 *   │   App.jsx  │
 *   └────┬───────┘
 *        │
 *        ▼
 * (1) App boots → Calls Google Maps Loader
 *        │
 *        ▼
 * (2) Google Maps Loaded → App waits for userLocation
 *        │
 *        ▼
 * (3) userLocation becomes ready (via IP lookup or geolocation)
 *        │
 *        ▼
 * (4) App renders <MapContainer userLocation=... onMapLoad=... />
 *        │
 *        ▼
 * (5) MapContainer:
 *         - Creates map ONLY ONCE if userLocation is valid
 *         - Calls onMapLoad(mapInstance)
 *        │
 *        ▼
 * (6) App's onMapLoad receives the new map instance → sets internal `map` state
 *        │
 *        ▼
 * (7) App now safe to:
 *     - Add markers
 *     - Center map properly
 *      - Start user interactions
 */
const MapContainer: React.FC<MapContainerProps> = ({
  userLocation,
  widget,
  onMapLoad,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  useEffect(() => {
    if (!userLocation || !mapRef.current || mapInstance.current) return;

    if (!window.google?.maps?.Map) {
      console.error("Google Maps SDK not loaded");
      return;
    }

    let lat, lng;
    if (
      Array.isArray(userLocation.location) &&
      userLocation.location.length === 2
    ) {
      [lat, lng] = userLocation.location;
    } else {
      console.error("Invalid userLocation format:", userLocation);
      return;
    }

    // Only create the map ONCE
    mapInstance.current = new google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 8,
      clickableIcons: false,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      gestureHandling: "greedy",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });

    onMapLoad(mapInstance.current);
  }, [userLocation, onMapLoad]);

  useWhyDidYouRender("MapContainer", {
    userLocation,
    onMapLoad,
  });

  return (
    <div
      id="map-container"
      className={widget === true ? "widget-container" : "non-widget-container"}
      ref={mapRef}
    />
  );
};

export default MapContainer;
